import React, { useState, useEffect } from "react";
import { Column } from "@amzn/meridian";
import StepRenderer from "../StepRenderer";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { setCurrentIterateStep, setIterationProgress } from "src/store/workflowSlice";
import { setScannedBarcode } from "src/store/inspectionCaptureSlice";
import { CameraStreamProps } from "../types/cameraStreamProps";
import { WASTE_LABEL_TARGETS } from "src/constants/constants";
import inspectionService from "src/services/inspectionService";

interface IterateStepProps {
  iterateSteps: any[];
  cameraStreamProps: CameraStreamProps;
  /** Ends the open-ended container loop (surfaced on the barcode sub-step). */
  onFinish?: () => void;
}

/**
 * True for the detect_object sub-step that captures the waste label. This is the
 * step skipped for a container found in workflow.skippableContainers
 */
const isWasteLabelStep = (step: any): boolean =>
  step?.type === "detect_object" &&
  Array.isArray(step?.target) &&
  step.target.some((target: string) => WASTE_LABEL_TARGETS.includes(target));

/**
 * When a container skips its label capture step, the label questions would
 * otherwise never be analysed. The backend provides `label_skip_additions` on the
 * detect_object step listing the extra targets and question ids to fold into the
 * container image analysis so those questions are still evaluated (and thus whether
 * the container qualifies to skip again next inspection).
 *
 * Returns the step unchanged when it has no additions to fold in.
 */
const augmentSkippableDetectStep = (step: any): any => {
  const additions = step?.label_skip_additions;
  if (!additions) return step;

  const mergedTargets = Array.from(
    new Set([...(step.target ?? []), ...(additions.targets ?? [])])
  );
  const mergedQuestionIds = Array.from(
    new Set([...(step.backend?.question_ids ?? []), ...(additions.question_ids ?? [])])
  );

  return {
    ...step,
    target: mergedTargets,
    backend: { ...step.backend, question_ids: mergedQuestionIds },
  };
};

const IterateStep: React.FC<IterateStepProps> = ({
  iterateSteps,
  cameraStreamProps,
  onFinish,
}) => {
  const dispatch = useAppDispatch();
  const { resumeIterateStepIndex, resumeCompletedContainerCount, inspectionId, skippableContainers } =
    useAppSelector((s) => s.workflow);
  const { scannedBarcode } = useAppSelector((s) => s.inspectionCapture);
  const [currentIteration, setCurrentIteration] = useState(resumeCompletedContainerCount);
  const [currentStepIndex, setCurrentStepIndex] = useState(resumeIterateStepIndex);

  const currentStep = iterateSteps[currentStepIndex];

  // Update Redux with the current iterate step whenever it changes
  useEffect(() => {
    dispatch(setCurrentIterateStep(currentStep));
  }, [currentStepIndex, dispatch, currentStep]);

  // Update Redux with the current container number so the header can show it.
  // The loop is open-ended, so `total` just reflects the count reached so far.
  useEffect(() => {
    dispatch(setIterationProgress({
      currentIndex: currentIteration,
      total: currentIteration + 1,
    }));
  }, [currentIteration, dispatch]);

  /** Moves to a specific sub-step within the current container. */
  const goToStep = (index: number) => {
    dispatch(setCurrentIterateStep(iterateSteps[index]));
    setCurrentStepIndex(index);
  };

  /**
   * Finished all sub-steps for this container. Automatically starts the next
   * container — the loop is open-ended and only ends when the user taps
   * "All Containers Finished" (handled by WorkflowExecutor).
   */
  const startNextContainer = () => {
    dispatch(setCurrentIterateStep(iterateSteps[0]));
    dispatch(setScannedBarcode(null));
    setCurrentIteration((prev) => prev + 1);
    setCurrentStepIndex(0);
  };

  const handleStepComplete = async () => {
    const isLastStep = currentStepIndex >= iterateSteps.length - 1;
    // read_barcode is always the first sub-step of a container, so wrapping into a
    // new container (nextStep = iterateSteps[0]) can never land on the label step —
    // the skip check below only ever fires when advancing within the SAME container.
    const nextIndex = isLastStep ? 0 : currentStepIndex + 1;
    const nextStep = iterateSteps[nextIndex];

    const sourceInspectionId = scannedBarcode ? skippableContainers[scannedBarcode] : undefined;

    if (isWasteLabelStep(nextStep) && scannedBarcode && sourceInspectionId) {
      try {
        await inspectionService.reuseLabelResult(inspectionId, scannedBarcode, sourceInspectionId);
        console.info(`Skipped label capture for container ${scannedBarcode}, reused results from inspection ${sourceInspectionId}`);
      } catch (err) {
        console.error("Failed to reuse label result; falling back to normal capture:", err);
        // Never silently drop the question -- render the label step normally so
        // the inspector still captures it.
        goToStep(nextIndex);
        return;
      }
      // Skip succeeded: advance PAST the label step instead of rendering it.
      const afterLabelIsLast = nextIndex >= iterateSteps.length - 1;
      afterLabelIsLast ? startNextContainer() : goToStep(nextIndex + 1);
      return;
    }

    isLastStep ? startNextContainer() : goToStep(nextIndex);
  };

  // A skippable container skips its label capture step, so fold the label
  // targets/question ids into its first detect_object step (see helper).
  const isSkippableContainer = !!(scannedBarcode && skippableContainers[scannedBarcode]);
  const effectiveStep = isSkippableContainer
    ? augmentSkippableDetectStep(currentStep)
    : currentStep;

  return (
    <Column>
      <StepRenderer
        step={effectiveStep}
        onStepComplete={handleStepComplete}
        cameraStreamProps={cameraStreamProps}
        onFinish={onFinish}
      />
    </Column>
  );
};

export default IterateStep;
